import { Box, Typography, useTheme } from "@mui/material";
import { ScreenSmallerThen } from "../../utilities/UIHelper";
import { PieChart } from "./pie";
import "../organization/overview/charts.css";

type ChartProps = {
  borderTopLeftRadius?: string;
  borderTopRightRadius?: string;
  borderBottomLeftRadius?: boolean;
  borderBottomRightRadius?: boolean;
  borderTop?: string;
  borderRight?: string;
  borderLeft?: string;
  borderBottom?: string;
  title?: string;
  tooltip: string;
  type: "donut" | "bar" | "line" | "pie" | "rangeBar" | "radar" | "heatmap";
  data:
    | number[]
    | { name: string; data: number[] }[]
    | { name: string; data: { x: string; y: number[] | number }[] }[];
  labels?: any;
  distributed?: boolean;
  stacked?: boolean;
  horizontal?: boolean;
  onClick: () => void;
  width?: string;
  colors?: string[];
  money?: boolean;
  xAxisTitle?: string;
  yAxisTitle?: string;
  min?: number | ((min: number) => number);
  max?: number | ((max: number) => number);
  onLegendClick?: (series: number[]) => void;
  id?: string;
  rangeBarGroupRows?: boolean;
  xAxisType?: "datetime" | "category";
  hideYAxisLabel?: boolean;
  bgcolor?: string;
  titleAlign?: "inherit" | "left" | "center" | "right" | "justify";
  noData?: ApexNoData;
};

const Chart = (props: ChartProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const {
    borderTopLeftRadius = "0px",
    borderTopRightRadius = "0px",
    borderBottomLeftRadius = false,
    borderBottomRightRadius = false,
    borderRight = "1px solid",
    borderLeft = "1px solid",
    borderTop = "1px solid",
    borderBottom = "1px solid",
    title,
    tooltip,
    money = false,
    type,
    data,
    labels,
    distributed = false,
    stacked = false,
    horizontal = false,
    onClick,
    colors = [
      theme.palette.custom.greenTypography,
      theme.palette.custom.yellowTypography,
      theme.palette.custom.redTypography,
    ],
    width,
    xAxisTitle,
    yAxisTitle,
    min,
    max,
    onLegendClick,
    id,
    rangeBarGroupRows = false,
    xAxisType,
    hideYAxisLabel = false,
    bgcolor,
    titleAlign = "left",
    noData,
  } = props;
  const isSmallScreen = ScreenSmallerThen();
  return (
    <Box
      padding="10px"
      overflow="hidden"
      display="flex"
      flexGrow="1"
      justifyContent="flex-start"
      alignItems="center"
      flexDirection="column"
      height="300px"
      sx={{
        borderTopLeftRadius: borderTopLeftRadius,
        borderTopRightRadius: borderTopRightRadius,
        borderBottomLeftRadius: borderBottomLeftRadius || isSmallScreen ? "6px" : "0px",
        borderBottomRightRadius: borderBottomRightRadius || isSmallScreen ? "6px" : "0px",
      }}
      bgcolor={bgcolor ?? theme.palette.custom.primaryBackground}
      width="100%"
      borderRight={borderRight}
      borderLeft={borderLeft}
      borderTop={borderTop}
      borderBottom={borderBottom}
      borderColor={theme.palette.custom.primaryBorder}
    >
      {title && (
        <Typography variant="h2" width="100%" textAlign={titleAlign}>
          {title}
        </Typography>
      )}
      <Box
        display="flex"
        flexGrow={1}
        flexDirection="column"
        justifyContent="center"
        sx={{
          ...(width === "100%" && { width: "100%" }),
        }}
      >
        <PieChart
          type={type}
          width={width}
          options={{
            plotOptions: {
              bar: {
                borderRadius: 3,
                horizontal: horizontal,
                distributed: distributed,
                rangeBarGroupRows: rangeBarGroupRows,
              },
            },
            stroke: {
              show: true,
              ...(type === "bar" && {
                colors: ["transparent"],
                width: 2,
              }),
            },
            dataLabels: {
              enabled: false,
            },
            xaxis: {
              categories: labels,
              labels: {
                rotate: 0,
                style: {
                  colors: theme.palette.custom.primaryTypography,
                },
                datetimeUTC: false,
                datetimeFormatter: {
                  year: "yyyy",
                  month: "MMM yyyy",
                  day: "dd MMM",
                  hour: "HH:mm",
                },
              },
              title: {
                text: xAxisTitle,
                style: {
                  color: theme.palette.custom.primaryTypography,
                },
              },
              type: xAxisType,
            },
            yaxis: {
              axisBorder: {
                show: true,
                color: "transparent",
              },
              labels: {
                show: !hideYAxisLabel,
                formatter: function (val: number) {
                  try {
                    if (typeof val === "number" && money) {
                      return "$" + val.toFixed(0);
                    }
                    return `${val}`;
                  } catch (e) {
                    return `${val}`;
                  }
                },
                style: {
                  colors: theme.palette.custom.primaryTypography,
                },
              },
              title: {
                text: yAxisTitle,
                style: {
                  color: theme.palette.custom.primaryTypography,
                },
              },
              min: min,
              max: max,
              forceNiceScale: true,
            },
            colors: colors,
            labels: labels,
            legend: {
              show: !hideYAxisLabel,
              labels: {
                colors: theme.palette.custom.primaryTypography,
              },
            },
            tooltip: {
              theme: theme.palette.mode,
            },
          }}
          data={data}
          labels={labels}
          stacked={stacked}
          onClick={onClick}
          onLegendClick={onLegendClick}
          id={id}
          noData={noData}
        />
      </Box>
    </Box>
  );
};

export { Chart };
